import { createAttendancePrompt, type AttendancePrompt } from './campaign-attendance-prompt';
import { campaignSessionWindowContains, parseCampaignSessionUtc } from '../janus-engine/movement/campaign-arrival';
import { effectiveFavor, favorTier } from '../janus-engine/inventory/merchant-favor';
import { _ftCamField } from '../shared/campaign-field';
// campaign-init.ts — MERCHANT FAVOR PREVIEW + VIDEO CAROUSELS + CAMPAIGN ATTENDANCE POLLING, carved out of
// template.js and TS-ported for Phase 2a of plans/bundler-script-load-order-phase2.md — a member of the
// post-template-mid aggregate (prepended; footer order ~1142, above campaign-time-init + roll-init).
//
// MODULE-SCOPE SAFE: the file documents "Side effects on load: NONE" — at eval only the FT_CAMPAIGN_ATT_*
// consts + the timer-id `let` + function defs + 6 window attaches. Deps (BASE_URL / ftDebugLog /
// getElementValue (core-helpers + template.js), bootstrap.Modal (CDN)) are all call-time
// (post-DOMContentLoaded). The 6 public exports are consumed by loadPostFunctions() (template.js) + the PHP
// footer IIFE; every other top-level fn/const is module-private (grep-verified: no cross-file consumer,
// including the deprecated fetchCampaignSessionStartAndEndTimes / checkCampaignSessionAttended shims).

// =============================================================================
// MERCHANT FAVOR / VIDEO CAROUSELS / CAMPAIGN ATTENDANCE
// =============================================================================
const campaignEconomyBound = new WeakSet<HTMLElement>();

function initMerchantFavorGmPreview(): void {
    document.querySelectorAll<HTMLElement>('[data-ft-campaign-economy]').forEach(root => {
        if (campaignEconomyBound.has(root)) return;
        const tier = root.querySelector<HTMLElement>('[data-ft-economy-tier]');
        const note = root.querySelector<HTMLElement>('[data-ft-economy-note]');
        if (!tier || !note) return;
        campaignEconomyBound.add(root);
        const update = (raw: unknown): void => {
            const [label = '', explanation = ''] = favorTier(effectiveFavor(raw)).split(' — ');
            if (tier.textContent !== label) tier.textContent = label;
            if (note.textContent !== explanation) note.textContent = explanation;
            if (note.hidden !== (explanation === '')) note.hidden = explanation === '';
        };
        update(root.dataset.economy);
        window.addEventListener('ft:campaign-state-received', event => {
            const detail = (event as CustomEvent<{ campaignId?: string; campaignArr?: Parameters<typeof _ftCamField>[0] }>).detail;
            if (!detail?.campaignArr || (detail.campaignId ?? _ftCamField(detail.campaignArr, 'campaign_id')) !== root.dataset.campaignId) return;
            update(_ftCamField(detail.campaignArr, 'campaign_economy_difficulty'));
        });
    });
    document.querySelectorAll('.merchant-favor-modify-section:not(.merchant-favor-preview-initialized)').forEach((root) => {
        root.classList.add('merchant-favor-preview-initialized');
        ftBindMerchantEconomyPreview(root as HTMLElement, 'merchant_favor', 'Favor');
    });
    document.querySelectorAll('.campaign-economy-difficulty-section:not(.merchant-favor-preview-initialized)').forEach((root) => {
        root.classList.add('merchant-favor-preview-initialized');
        ftBindMerchantEconomyPreview(root as HTMLElement, 'campaign_economy_difficulty', 'Economy');
    });
}

function ftBindMerchantEconomyPreview(root: HTMLElement, inputId: string, titleLabel: string): void {
    const num = root.querySelector('#' + inputId) as HTMLInputElement | null;
    const range = root.querySelector('#' + inputId + 'Range') as HTMLInputElement | null;
    const out = root.querySelector('.merchant-favor-gm-preview');
    if (!num || !out) return;

    function updatePreview(): void {
        const f = effectiveFavor(num!.value);
        const buyMult = 4 - (f / 100) * 3;
        const sellPct = 100 + (f / 100) * 20;
        const listGp = 100;
        const buyGp = listGp * buyMult;
        const sellGp = listGp * (sellPct / 100);
        out!.innerHTML =
            '<div><strong>' + titleLabel + ' ' + f + '</strong> — ' + favorTier(f) + '</div>' +
            '<div>Buy multiplier: ' + buyMult.toFixed(1) + '× (you pay ' + buyGp.toFixed(2) + ' gp for a ' + listGp + ' gp list item)</div>' +
            '<div>Merchant pays you when you sell: ' + sellPct.toFixed(0) + '% of list → ' + sellGp.toFixed(2) + ' gp for a ' + listGp + ' gp list item</div>';
    }

    num.addEventListener('input', updatePreview);
    num.addEventListener('change', updatePreview);
    if (range) {
        range.addEventListener('input', updatePreview);
        range.addEventListener('change', updatePreview);
    }
    updatePreview();
}


function initializeVideoCarousels(): void {
    const videoCarousels = document.querySelectorAll('.video-carousel:not(.initialized)');
    videoCarousels.forEach((carousel) => {
        const videos = carousel.querySelectorAll('video');
        let isVisible = false;
        let isHovered = false;

        videos.forEach((video) => {
            video.autoplay = false;
            video.loop = false;
            video.addEventListener('ended', () => {
                video.currentTime = 0;
                video.pause();
                carousel.classList.add('has-been-visible');
            });
        });

        const playActiveVideo = () => {
            const activeVideo = carousel.querySelector('.carousel-item.active video') as HTMLVideoElement | null;
            if (activeVideo && activeVideo.paused) {
                activeVideo.play().catch((e) => console.error('Play error:', e));
            }
        };

        const pauseAndResetActiveVideo = () => {
            const activeVideo = carousel.querySelector('.carousel-item.active video') as HTMLVideoElement | null;
            if (activeVideo) {
                activeVideo.pause();
                activeVideo.currentTime = 0;
            }
        };

        const updatePlayback = () => {
            const activeVideo = carousel.querySelector('.carousel-item.active video') as HTMLVideoElement | null;
            if (!activeVideo) return;

            if (isHovered) {
                activeVideo.loop = true;
            } else if (isVisible && !carousel.classList.contains('has-been-visible')) {
                activeVideo.loop = false;
            } else {
                activeVideo.pause();
                return;
            }

            if (activeVideo.paused) {
                activeVideo.play().catch((e) => console.error('Play error:', e));
            }
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const wasVisible = isVisible;
                isVisible = entry.isIntersecting;

                if (isVisible && !wasVisible) {
                    // First time becoming visible
                    updatePlayback();
                } else {
                    if (!isVisible) {
                        videos.forEach((v) => v.pause());
                    }
                    updatePlayback();
                }
            });
        }, { threshold: 0.5 });
        observer.observe(carousel);

        carousel.addEventListener('mouseenter', () => {
            carousel.classList.remove('has-been-visible');
            isHovered = true;
            updatePlayback();
        });

        carousel.addEventListener('mouseleave', () => {
            isHovered = false;
            pauseAndResetActiveVideo();
            carousel.classList.add('has-been-visible');
            updatePlayback(); // In case it's visible after mouseout
        });

        carousel.addEventListener('slide.bs.carousel', () => {
            videos.forEach((v) => v.pause());
        });

        carousel.addEventListener('slid.bs.carousel', () => {
            updatePlayback();
        });

        carousel.classList.add('initialized');


    });
}


// Shared across rescans so asynchronously injected cards use the same near-viewport preload band.
let videoCardPreloadObserver: IntersectionObserver | null = null;

function initializeVideoCards(): void {
    const videoCards = document.querySelectorAll('.video-card-hover:not(.initialized)');
    videoCards.forEach((cardNode) => {
        const card = cardNode as HTMLElement;
        const video = card.querySelector('video');
        if (!video) {
            card.classList.add('initialized');
            return;
        }

        if (video.dataset.ftLazyPreload === 'metadata') {
            videoCardPreloadObserver ??= new IntersectionObserver((entries, observer) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    const pending = entry.target as HTMLVideoElement;
                    observer.unobserve(pending);
                    if (pending.dataset.ftLazyPreload !== 'metadata') return;
                    delete pending.dataset.ftLazyPreload;
                    pending.preload = 'metadata';
                    // Hover/intro may already have started loading. Never reset that playback.
                    if (pending.readyState === 0 && pending.networkState !== pending.NETWORK_LOADING) {
                        pending.load();
                    }
                });
            }, { rootMargin: '600px 0px' });
            videoCardPreloadObserver.observe(video);
        }

        video.loop = false;
        video.muted = true;
        video.setAttribute('playsinline', '');

        video.addEventListener('ended', () => {
            video.currentTime = 0;
            video.pause();
        });

        // Overlay fade while playing (Fable 2026-07-01 — owner feature: "as it plays it fades, when
        // it stops it returns"). Driven PURELY off media events so intro autoplay, hover-play, and
        // tap-replay all behave identically. The CSS (30-main-body.css) keys on
        // .ft-fade-overlay-on-play.ft-overlay-faded — an opt-in marker only the classes/subclasses
        // card builder emits — so this toggle is inert on adventures/library/source video cards.
        video.addEventListener('playing', () => card.classList.add('ft-overlay-faded'));
        video.addEventListener('pause', () => card.classList.remove('ft-overlay-faded'));

        // First time the card is meaningfully visible: autoplay one pass (no loop)
        let viewportIntroStarted = false;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        video.pause();
                        return;
                    }
                    if (!viewportIntroStarted) {
                        viewportIntroStarted = true;
                        video.play().catch(() => {});
                    }
                });
            },
            { threshold: 0.35, rootMargin: '0px' }
        );
        io.observe(card);

        card.addEventListener('mouseenter', () => {
            video.play().catch(() => {});
        });

        card.addEventListener('mouseleave', () => {
            video.pause();
            video.currentTime = 0;
        });

        // Touch: replay on tap (light tap, not a long scroll)
        let touchStartY: number | null = null;
        card.addEventListener(
            'touchstart',
            (e: TouchEvent) => {
                if (e.touches && e.touches[0]) {
                    touchStartY = e.touches[0].clientY;
                }
            },
            { passive: true }
        );
        card.addEventListener(
            'touchend',
            (e: TouchEvent) => {
                if (touchStartY === null || !e.changedTouches || !e.changedTouches[0]) {
                    touchStartY = null;
                    return;
                }
                const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
                touchStartY = null;
                if (dy < 14) {
                    video.play().catch(() => {});
                }
            },
            { passive: true }
        );

        card.classList.add('initialized');
    });
}


// Carousels that already carry the slide.bs.carousel visibility handler. A WeakSet (not a data-*
// attribute) so a cloneNode/outerHTML copy — which copies attributes but never listeners — is bound afresh.
const carouselVisibilityBound = new WeakSet<Element>();

function initCarouselButtonVisibility(carouselSelector: string = '.carousel'): void {
    document.querySelectorAll<HTMLElement>(carouselSelector).forEach((carousel) => {
        // Bind once per carousel. loadPostFunctions() calls this on every AJAX inject / WS tick; before
        // 2026-08-27 there was no guard, so each pass stacked another slide.bs.carousel handler.
        if (carouselVisibilityBound.has(carousel)) {
            return;
        }
        carouselVisibilityBound.add(carousel);
        carousel.addEventListener('slide.bs.carousel', () => {
            const presentationInput = carousel.querySelector('.carousel-presentation-pic-id') as HTMLInputElement | null;
            const thumbnailInput = carousel.querySelector('.carousel-thumbnail-pic-id') as HTMLInputElement | null;
            const presentationPicId = presentationInput ? presentationInput.value : '';
            const thumbnailPicId = thumbnailInput ? thumbnailInput.value : '';

            // Update visibility of buttons in all carousel items
            carousel.querySelectorAll('.carousel-item').forEach((item) => {
                const presentationButton = item.querySelector('.set-presentation-pic') as HTMLElement | null;
                const thumbnailButton = item.querySelector('.set-thumbnail-pic') as HTMLElement | null;
                const picIdMatch = item.querySelector('.set-presentation-pic')?.getAttribute('onclick')?.match(/'([^']+)'/g);
                const currentPicId = picIdMatch?.[1]?.replace(/'/g, '') ?? '';

                if (presentationButton) {
                    presentationButton.style.display = (currentPicId === presentationPicId) ? 'none' : '';
                }
                if (thumbnailButton) {
                    thumbnailButton.style.display = (currentPicId === thumbnailPicId) ? 'none' : '';
                }
            });
        });
    });
}


const FT_CAMPAIGN_ATT_STORAGE_PREFIX = 'ft:v1:campaignAttendance:';
// A GM can schedule a session while a player's FT3D tab is already open. A one-hour negative
// cache made that new session invisible for the entire ready window; keep the quiet poll, but
// bound discovery lag to one minute.
const FT_CAMPAIGN_ATT_NONE_CACHE_MS = 60 * 1000;
// Poll a known future session too: the GM can insert an earlier session or reschedule the known
// one into the ready window. Six-hour trust made both edits invisible to an open tab.
const FT_CAMPAIGN_ATT_OUTSIDE_WINDOW_STALE_MS = 60 * 1000;
const FT_CAMPAIGN_ATT_NEAR_WINDOW_MS = 2 * 60 * 1000;

let ftCampaignAttendanceScheduleTimerId: number | null = null;
let ftAttendanceSyncVersion = 0;
let ftAttendanceCampaignId = '';
let ftAttendanceInitialized = false;

interface CampaignAttendanceContext extends Record<string, unknown> {
    status?: string;
    message?: string;
    next_campaign_session_id?: string;
    next_campaign_session_start_time?: string;
    next_campaign_session_end_time?: string;
    attended?: string | number | null;
    in_attendance_window?: boolean;
    require_ready_click?: boolean;
    caster_active_in_campaign_session?: string;
    confirmed_campaign_session_id?: string;
    confirmed_campaign_session_end_time?: string;
    code?: string;
}

interface CampaignAttendanceCache {
    savedAt: number;
    ctx: CampaignAttendanceContext;
}

function attendanceContextOf(value: unknown): CampaignAttendanceContext | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as CampaignAttendanceContext
        : null;
}

function ftCampaignAttendanceStorageKey(campaignId: string): string {
    return FT_CAMPAIGN_ATT_STORAGE_PREFIX + String(campaignId);
}

function readCampaignAttendanceSessionStorage(campaignId: string): CampaignAttendanceCache | null {
    if (typeof sessionStorage === 'undefined' || !campaignId) {
        return null;
    }
    try {
        const raw = sessionStorage.getItem(ftCampaignAttendanceStorageKey(campaignId));
        if (!raw) {
            return null;
        }
        const parsed: unknown = JSON.parse(raw);
        const o = attendanceContextOf(parsed);
        const ctx = attendanceContextOf(o?.ctx);
        if (!o || !ctx) {
            return null;
        }
        return {
            savedAt: typeof o.savedAt === 'number' ? o.savedAt : 0,
            ctx,
        };
    } catch (e) {
        return null;
    }
}

function writeCampaignAttendanceSessionStorage(campaignId: string, ctx: CampaignAttendanceContext): void {
    if (typeof sessionStorage === 'undefined' || !campaignId || !ctx) {
        return;
    }
    try {
        sessionStorage.setItem(
            ftCampaignAttendanceStorageKey(campaignId),
            JSON.stringify({ savedAt: Date.now(), ctx })
        );
    } catch (e) {
        /* quota / private mode */
    }
}

function clearCampaignAttendanceSchedule(): void {
    if (ftCampaignAttendanceScheduleTimerId !== null) {
        clearTimeout(ftCampaignAttendanceScheduleTimerId);
        ftCampaignAttendanceScheduleTimerId = null;
    }
}

function scheduleCampaignAttendanceSync(delayMs: number): void {
    clearCampaignAttendanceSchedule();
    const d = Math.max(0, delayMs | 0);
    ftCampaignAttendanceScheduleTimerId = setTimeout(() => {
        ftCampaignAttendanceScheduleTimerId = null;
        void syncCampaignSessionAttendance();
    }, d);
}

/**
 * A ready confirmation is scoped to the current session, not the lifetime of the browser tab.
 * Keep the confirmation scoped to its own end time and poll at least once per minute so an
 * overlapping or newly inserted earlier session can still issue its own ready call.
 */
function holdCampaignAttendanceForConfirmedSession(ctx: CampaignAttendanceContext | null): boolean {
    if (ftCampaignSessionAttendanceValue('caster_active_in_campaign_session') !== '1') {
        return false;
    }

    const endValue = ftCampaignSessionAttendanceValue('confirmed_campaign_session_end_time')
        || ctx?.next_campaign_session_end_time
        || ftCampaignSessionAttendanceValue('next_campaign_session_end_time');
    const endMs = parseCampaignSessionUtc(endValue);
    const now = Date.now() - ftCampaignServerClockOffsetMs;
    if (endMs !== null && now <= endMs) {
        scheduleCampaignAttendanceSync(Math.max(1000, Math.min(endMs - now + 1000, 60 * 1000)));
        return true;
    }

    // Missing/expired session identity must never turn "ready" into a permanent polling stop.
    ftSetCampaignSessionAttendanceContext({
        caster_active_in_campaign_session: '',
        confirmed_campaign_session_id: '',
        confirmed_campaign_session_end_time: '',
    }, 'attendance-session-ended');
    return false;
}

/** Drop cached attendance snapshot for one campaign (e.g. user switched campaign). */
function ftClearCampaignAttendanceSessionStorage(campaignId: string): void {
    if (typeof sessionStorage === 'undefined' || !campaignId) {
        return;
    }
    try {
        sessionStorage.removeItem(ftCampaignAttendanceStorageKey(campaignId));
    } catch (e) {}
}

function shouldSkipCampaignAttendanceNetworkFetch(cached: CampaignAttendanceCache | null): boolean {
    if (!cached || !cached.ctx) {
        return false;
    }
    const ctx = cached.ctx;
    const age = Date.now() - (cached.savedAt || 0);
    if (ctx.status === 'none') {
        return age < FT_CAMPAIGN_ATT_NONE_CACHE_MS;
    }
    if (ctx.status !== 'success') {
        return false;
    }
    const tStart = parseCampaignSessionUtc(ctx.next_campaign_session_start_time);
    const tEnd = parseCampaignSessionUtc(ctx.next_campaign_session_end_time);
    if (tStart === null || tEnd === null) {
        return false;
    }
    const now = Date.now() - ftCampaignServerClockOffsetMs;
    if (now > tEnd) {
        return false;
    }
    if (now >= tStart && now <= tEnd) {
        return false;
    }
    if (now < tStart) {
        return tStart - now > FT_CAMPAIGN_ATT_NEAR_WINDOW_MS && age < FT_CAMPAIGN_ATT_OUTSIDE_WINDOW_STALE_MS;
    }
    return false;
}

function planNextCampaignAttendanceSync(ctx: CampaignAttendanceContext | null): void {
    const active = ftCampaignSessionAttendanceValue('caster_active_in_campaign_session');
    if (active === '1') {
        if (holdCampaignAttendanceForConfirmedSession(ctx)) {
            return;
        }
    }
    if (active === '0') {
        scheduleCampaignAttendanceSync(60 * 1000);
        return;
    }

    const campaignId = getElementValue('campaign_id');
    if (!campaignId || campaignId.trim() === '') {
        clearCampaignAttendanceSchedule();
        return;
    }

    if (!ctx) {
        scheduleCampaignAttendanceSync(FT_CAMPAIGN_ATT_NONE_CACHE_MS);
        return;
    }

    if (ctx.status === 'error') {
        // 2026-09-03 playtest: a pending ready-click polled every 10 s per player for the whole
        // night (4,360 attendance requests from 7 players). The ready click itself re-syncs
        // immediately, so the background cadence only has to catch a GM-side change: 30 s.
        scheduleCampaignAttendanceSync(ctx.code === 'attendance_caster_pending' ? 30 * 1000 : 5 * 60 * 1000);
        return;
    }

    if (ctx.status === 'none') {
        scheduleCampaignAttendanceSync(FT_CAMPAIGN_ATT_NONE_CACHE_MS);
        return;
    }

    if (ctx.status !== 'success') {
        scheduleCampaignAttendanceSync(FT_CAMPAIGN_ATT_NONE_CACHE_MS);
        return;
    }

    const tStart = parseCampaignSessionUtc(ctx.next_campaign_session_start_time);
    const tEnd = parseCampaignSessionUtc(ctx.next_campaign_session_end_time);
    const now = Date.now() - ftCampaignServerClockOffsetMs;

    if (tStart === null || tEnd === null) {
        scheduleCampaignAttendanceSync(FT_CAMPAIGN_ATT_NONE_CACHE_MS);
        return;
    }

    if (now > tEnd) {
        // Past the scheduled end with the tab still open — the common late-night case. This was a
        // 5 s poll per player for the rest of the night; nothing here changes faster than a minute.
        scheduleCampaignAttendanceSync(60 * 1000);
        return;
    }

    if (now >= tStart && now <= tEnd) {
        scheduleCampaignAttendanceSync(60 * 1000);
        return;
    }

    if (now < tStart) {
        const msUntil = tStart - now;
        const capped = Math.min(msUntil - 60 * 1000, FT_CAMPAIGN_ATT_OUTSIDE_WINDOW_STALE_MS);
        scheduleCampaignAttendanceSync(Math.max(capped, 30 * 1000));
        return;
    }

    scheduleCampaignAttendanceSync(60 * 1000);
}

function ftCampaignSessionAttendanceContext(): CampaignAttendanceContext {
    return window.ftGetCampaignSessionAttendance?.() || window.FateTide?.sessionAttendance || {};
}

function ftCampaignSessionAttendanceValue(key: string): string {
    const ctx = ftCampaignSessionAttendanceContext();
    return String(ctx[key] ?? '').trim();
}

function ftSetCampaignSessionAttendanceContext(ctx: CampaignAttendanceContext, source: string): CampaignAttendanceContext {
    if (window.ftSetCampaignSessionAttendance) {
        return window.ftSetCampaignSessionAttendance(ctx, source);
    }
    window.FateTide = window.FateTide || {};
    window.FateTide.sessionAttendance = Object.assign({}, window.FateTide.sessionAttendance || {}, ctx, {
        fetchedAt: Date.now(),
        source,
    });
    return window.FateTide.sessionAttendance;
}

async function processCampaignSessionAttendanceAfterApply(ctx: CampaignAttendanceContext): Promise<void> {
    // A live cancellation, reschedule, expiry, or confirmation in another tab retires the old
    // prompt. Network errors are not an authoritative cancellation and leave retry available.
    if ((ctx.status === 'none' || ctx.status === 'success') && (!ctx.require_ready_click || !ctx.in_attendance_window)) {
        ftAttendancePrompt?.close();
        ftAttendancePrompt = null;
        if (ftCampaignSessionAttendanceValue('caster_active_in_campaign_session') === '0') {
            ftSetCampaignSessionAttendanceContext({ caster_active_in_campaign_session: '' }, 'attendance-prompt-retired');
        }
    }
    if (ctx.status !== 'success') {
        if (ctx.message && ctx.status !== 'none') {
            ftDebugLog('session attendance', { message: ctx.message, at: new Date().toISOString() });
        }
        return;
    }

    if (ctx.attended === undefined || ctx.attended === null) {
        return;
    }

    if (!ctx.in_attendance_window) {
        return;
    }

    // The server may report attended=1 with an inactive caster when it detects/repairs a
    // historical duplicate-row race. Do not let the attended bit short-circuit the explicit
    // rejoin prompt; the repeated ready POST is intentionally the repair operation.
    if (ctx.require_ready_click) {
        ftSetCampaignSessionAttendanceContext({ caster_active_in_campaign_session: '0' }, 'attendance-ready-required');
        await markCampaignSessionAttended();
        return;
    }

    const attendedNum = parseInt(String(ctx.attended), 10);
    if (attendedNum === 1) {
        ftSetCampaignSessionAttendanceContext({
            caster_active_in_campaign_session: '1',
            confirmed_campaign_session_id: ctx.next_campaign_session_id || '',
            confirmed_campaign_session_end_time: ctx.next_campaign_session_end_time || '',
        }, 'attendance-attended');
    } else {
        ftSetCampaignSessionAttendanceContext({ caster_active_in_campaign_session: '1' }, 'attendance-auto-attended');
    }
}

function applyCampaignSessionAttendanceContext(ctx: CampaignAttendanceContext): void {
    // Stored contexts are snapshots, never fresh clock samples.
    if (ctx.status === 'success' && ctx.next_campaign_session_id) {
        ftSetCampaignSessionAttendanceContext({
            ...ctx,
            next_campaign_session_id: ctx.next_campaign_session_id,
            next_campaign_session_start_time: ctx.next_campaign_session_start_time || '',
            next_campaign_session_end_time: ctx.next_campaign_session_end_time || '',
        }, 'attendance-context');
    } else {
        ftSetCampaignSessionAttendanceContext({
            ...ctx,
            require_ready_click: false,
            in_attendance_window: false,
            attended: null,
            caster_id: '',
            ready_deadline: '',
            next_campaign_session_id: '',
            next_campaign_session_start_time: '',
            next_campaign_session_end_time: '',
        }, 'attendance-context-clear');
    }
}

/**
 * Hydrate FateTide.sessionAttendance from sessionStorage so upcoming sessions survive reloads
 * and long-lived tabs without hammering get_campaign_session_attendance_context.php.
 */
function hydrateCampaignAttendanceFromSessionStorage(campaignId: string): void {
    const trimmed = campaignId && String(campaignId).trim();
    if (!trimmed) {
        return;
    }
    const cached = readCampaignAttendanceSessionStorage(trimmed);
    if (!cached || !cached.ctx) {
        return;
    }
    applyCampaignSessionAttendanceContext(cached.ctx);
}

/**
 * The caster id the attendance check must run against = the player's OWN character on this sheet
 * (the MASTER caster), NEVER an active-summon override. The old `getElementValue('caster_id')` read
 * window.FateTide.activeCasterId, which the sidelog seed sets to a selected summon when one is active
 * (see sidelog.php) — so the attendance/late-activation check could land on a summon's id and silently
 * do nothing. Resolve master first (window.FateTide.masterCasterId via ftGetMasterCasterId), then fall
 * back to active (covers a campaign PC that is NOT in an encounter, where master is unseeded and active
 * = the PC's item_id). Pure cache reads — both are seeded on the item.php PC sheet at first paint.
 */
function ftAttendanceCasterId(): string {
    const master = window.ftGetMasterCasterId ? String(window.ftGetMasterCasterId() || '').trim() : '';
    if (master) {
        return master;
    }
    return window.ftGetActiveCasterId ? String(window.ftGetActiveCasterId() || '').trim() : '';
}

// ---------------------------------------------------------------------------------------------
// LOCAL attendance evaluation (2026-09-03, plans/attendance-frontend-no-polling-2026-09-03.md
// slice 1). The campaign blob every client holds (window.FateTide.campaignCache, refreshed by the
// campaign_update push) now carries a lean `campaign_sessions` embed — the sessions in/near their
// window, each with prompt_start / session_end from the server's own rules, the party-visible
// attendance rows and the cancelled set — plus `campaign_players` with the inactive flag. From
// that, this mirrors get_campaign_session_attendance_context.php's candidate selection and verdict
// exactly, so the browser never asks the server "am I in the window". Every write that changes
// the inputs publishes a fresh blob (ft_publish_campaign_state); the network fetch remains only as
// the fallback when the cache has no embed (old blob / unseeded page).
// ---------------------------------------------------------------------------------------------
/** Date.now() − server UTC now, captured from each pushed campaign blob (0 until one arrives). */
let ftCampaignServerClockOffsetMs = 0;
/** Last processed verdict + when, so a 6 s clock-tick push does not re-arm the ready flow every time. */
let ftCampaignAttendanceLastSignature = '';
let ftCampaignAttendanceLastProcessedAt = 0;

function ftCampaignAttendanceSignature(ctx: CampaignAttendanceContext): string {
    return [
        ctx.status ?? '', ctx.code ?? '', ctx.next_campaign_session_id ?? '', ctx.caster_id ?? '',
        ctx.next_campaign_session_start_time ?? '', ctx.next_campaign_session_end_time ?? '', ctx.ready_deadline ?? '',
        String(ctx.attended ?? ''), ctx.in_attendance_window ? '1' : '0', ctx.require_ready_click ? '1' : '0',
    ].join('|');
}

interface FtCampaignSessionEmbed {
    campaign_session_id: string;
    campaign_session_start_time: string;
    prompt_start: string | null;
    session_end: string | null;
    grace_seconds: number;
    attendance: Array<{ user_id: string; attended: number }>;
    cancelled_user_ids: string[];
    unsettled_user_ids?: string[];
}

/**
 * Last settle attempt per session (ms). The server is idempotent and owns the boundary (a call in the
 * grace second returns zero rows / too_early), so a tab retries every 30 s while the pushed embed still
 * lists unsettled owners — and stops by itself once the push shows the session settled.
 */
const ftCampaignSettleLastAttemptAt = new Map<string, number>();

function ftMaybeSettleCampaignSessions(sessions: FtCampaignSessionEmbed[], isGm: boolean, now: number): void {
    for (const s of sessions) {
        if (!Array.isArray(s.unsettled_user_ids) || s.unsettled_user_ids.length === 0) continue;
        const start = parseCampaignSessionUtc(s.campaign_session_start_time);
        const end = parseCampaignSessionUtc(s.session_end || '');
        if (start === null || end === null) continue;
        const graceEnd = start + Math.max(0, Number(s.grace_seconds) || 0) * 1000;
        // Only a session IN PROGRESS is settled — never a stale one inside the lookback.
        if (now <= graceEnd + 1000 || now > end) continue;
        const last = ftCampaignSettleLastAttemptAt.get(s.campaign_session_id) ?? 0;
        if (Date.now() - last < 30 * 1000) continue;
        ftCampaignSettleLastAttemptAt.set(s.campaign_session_id, Date.now());
        const delay = isGm ? 0 : 3000 + Math.floor(Math.random() * 7000);
        window.setTimeout(() => {
            void (async () => {
                const endpoint = BASE_URL + 'options/tools/campaign_sessions_attended/settle_campaign_session_attendance.php';
                const post = async (body: Record<string, unknown>) => {
                    const response = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ campaign_session_id: s.campaign_session_id, ...body }), credentials: 'same-origin' });
                    return response.json() as Promise<Record<string, unknown>>;
                };
                const inputs = await post({ operation: 'placement-inputs', owner_ids: s.unsettled_user_ids });
                if (inputs.status !== 'success') throw new Error(String(inputs.message ?? 'attendance:inputs-unavailable'));
                const planner = (window as Window & { ftPlanArrivalPlacement?: (input: Record<string, unknown>) => Promise<Record<string, unknown>> }).ftPlanArrivalPlacement;
                if (!planner) throw new Error('attendance:placement-planner-unavailable');
                const placement_plan = inputs.source_ids instanceof Array && inputs.source_ids.length
                    ? await planner(inputs) : {};
                const result = await post({ placement_plan });
                ftDebugLog('session attendance', { settle: s.campaign_session_id, result });
            })().catch(error => console.error('[attendance-placement]', error));
        }, delay);
    }
}

function ftCampaignBlobScalar(arr: Record<string, unknown> | undefined, key: string): string {
    if (!arr) return '';
    const bucket = arr.campaigns;
    if (bucket && typeof bucket === 'object' && key in (bucket as Record<string, unknown>)) {
        const v = (bucket as Record<string, unknown>)[key];
        return v == null ? '' : String(v);
    }
    const wrapped = arr[key];
    if (Array.isArray(wrapped)) return wrapped[0] == null ? '' : String(wrapped[0]);
    return wrapped == null ? '' : String(wrapped);
}

function evaluateCampaignAttendanceLocally(campaignId: string): CampaignAttendanceContext | null {
    // window.FateTide is declared per-bundle; campaignCache / user are seeded by the websocket +
    // page bootstraps and are read here through a structural cast.
    const ft = window.FateTide as unknown as {
        campaignCache?: Map<string, unknown>;
        user?: { id?: string };
    } | undefined;
    const camp = ft?.campaignCache?.get(campaignId) as (Record<string, unknown> & {
        campaign_sessions?: unknown;
        campaign_players?: unknown;
    }) | undefined;
    if (!camp || !Array.isArray(camp.campaign_sessions)) return null;
    const userId = String(ft?.user?.id || '').trim().toUpperCase();
    if (!userId) return null;

    const gmId = ftCampaignBlobScalar(camp, 'campaign_gm').toUpperCase();
    const isGm = gmId !== '' && gmId === userId;

    // The user's OWN attached PC, resolved like ft_resolve_campaign_attendance_caster_id(): the
    // sheet's own caster when it is one of my roster rows, else my first roster row.
    const players = Array.isArray(camp.campaign_players) ? camp.campaign_players as Array<Record<string, unknown>> : [];
    const mine = players.filter(p => String(p.player_character_created_by_id ?? '').toUpperCase() === userId);
    const isPlayer = mine.length > 0;
    const preferred = ftAttendanceCasterId().toUpperCase();
    const pick = mine.find(p => String(p.item_id ?? '').toUpperCase() === preferred) ?? (mine.length === 1 ? mine[0] : null);
    // Multiple owned PCs need the server's unambiguous resolver, never the first roster entry.
    if (!isGm && mine.length > 1 && !pick) return null;
    const casterId = pick ? String(pick.item_id ?? '').toUpperCase() : '';
    const casterInactive = pick ? Number(pick.player_character_inactive ?? 0) === 1 : false;
    const casterEligible = !isGm && casterId !== '';
    // Server-corrected clock: the blob carries computed.campaign.server_utc_now, stamped on every
    // publish and on the 6 s tick; the offset is captured when the frame arrives.
    const now = Date.now() - ftCampaignServerClockOffsetMs;
    const attendedIn = (s: FtCampaignSessionEmbed): boolean =>
        (s.attendance || []).some(r => String(r.user_id).toUpperCase() === userId && Number(r.attended) === 1);

    let chosen: FtCampaignSessionEmbed | null = null;
    let satisfied: FtCampaignSessionEmbed | null = null;
    let fallback: FtCampaignSessionEmbed | null = null;
    for (const s of camp.campaign_sessions as FtCampaignSessionEmbed[]) {
        const ps = parseCampaignSessionUtc(s.prompt_start || '');
        const se = parseCampaignSessionUtc(s.session_end || '');
        if (ps === null || se === null || se < now) continue;
        if ((s.cancelled_user_ids || []).some(u => String(u).toUpperCase() === userId)) continue;
        const within = campaignSessionWindowContains(s, now);
        if (within) {
            if (!isGm && (!attendedIn(s) || (casterEligible && casterInactive))) {
                chosen = s;
                break;
            }
            // A confirmation publish may arrive before its HTTP response. Preserve that
            // authoritative attendance while still looking for an overlapping ready call.
            if (!isGm && casterEligible && attendedIn(s) && satisfied === null) satisfied = s;
            continue;
        }
        if (ps > now && fallback === null) fallback = s;
    }
    // Slice 3: any member's tab past a session's start + grace with unanswered owners asks the server
    // to settle it (idempotent there). GM first, players a few seconds later; once per tab per session.
    ftMaybeSettleCampaignSessions(camp.campaign_sessions as FtCampaignSessionEmbed[], isGm, now);

    chosen = chosen ?? satisfied ?? fallback;
    if (!chosen) {
        return { status: 'none', message: 'No upcoming or in-progress campaign sessions found' };
    }
    const start = parseCampaignSessionUtc(chosen.campaign_session_start_time);
    const grace = Number(chosen.grace_seconds);
    if (start === null || !Number.isFinite(grace) || grace < 0) return null;
    const within = campaignSessionWindowContains(chosen, now);
    if (!isGm && isPlayer && within && !casterEligible) {
        return { status: 'error', code: 'attendance_caster_pending', message: 'Player character is not ready for attendance confirmation' };
    }
    const attended = attendedIn(chosen) ? 1 : 0;
    return {
        status: 'success',
        next_campaign_session_id: chosen.campaign_session_id,
        next_campaign_session_start_time: chosen.prompt_start || '',
        next_campaign_session_end_time: chosen.session_end || '',
        in_attendance_window: within,
        attended,
        require_ready_click: !isGm && casterEligible && within && (attended === 0 || casterInactive),
        ready_deadline: new Date(start + grace * 1000).toISOString(),
        caster_id: casterEligible ? casterId : '',
    };
}

/** One round-trip: next session window + attendance state (replaces fetch + check). Uses cached context + scheduled wake-ups instead of a fixed 60s poll. */
async function syncCampaignSessionAttendance(): Promise<void> {
    const version = ++ftAttendanceSyncVersion;
    const campaignId = getElementValue('campaign_id');
    if (campaignId !== ftAttendanceCampaignId) {
        ftAttendancePrompt?.close();
        ftAttendancePrompt = null;
        ftAttendanceCampaignId = campaignId;
        ftCampaignServerClockOffsetMs = 0;
        ftCampaignAttendanceLastSignature = '';
        ftSetCampaignSessionAttendanceContext({ caster_active_in_campaign_session: '', confirmed_campaign_session_id: '', confirmed_campaign_session_end_time: '' }, 'attendance-campaign-change');
    }
    if (!campaignId || campaignId.trim() === '') {
        ftSetCampaignSessionAttendanceContext({
            next_campaign_session_id: '',
            next_campaign_session_start_time: '',
            next_campaign_session_end_time: '',
        }, 'attendance-no-campaign');
        clearCampaignAttendanceSchedule();
        return;
    }

    // This form lists unassigned PCs. Attendance starts on the sheet after the Link save and redirect.
    if (document.getElementById('link_player_character')) {
        clearCampaignAttendanceSchedule();
        return;
    }

    const active = ftCampaignSessionAttendanceValue('caster_active_in_campaign_session');
    if (active === '1') {
        // This schedules the safety wake and clears an expired confirmation, but an active
        // confirmation no longer suppresses this network sync: overlapping sessions need polling.
        holdCampaignAttendanceForConfirmedSession(ftCampaignSessionAttendanceContext());
    }

    // Local evaluation first: the pushed campaign blob is the truth; no request leaves the tab.
    const local = evaluateCampaignAttendanceLocally(campaignId);
    if (local) {
        applyCampaignSessionAttendanceContext(local);
        // Observability for smokes: proves the verdict came from the pushed blob, not the network.
        ftSetCampaignSessionAttendanceContext({ evaluated_locally_at: String(Date.now()) }, 'attendance-local');
        writeCampaignAttendanceSessionStorage(campaignId, local);
        // Process (arm the modal / confirm state) on a verdict TRANSITION, or at most once a minute
        // (the old poll cadence) — never on every 6 s clock-tick push.
        const sig = ftCampaignAttendanceSignature(local);
        const nowMs = Date.now();
        if (ftAttendancePrompt || sig !== ftCampaignAttendanceLastSignature || nowMs - ftCampaignAttendanceLastProcessedAt >= 60 * 1000) {
            ftCampaignAttendanceLastSignature = sig;
            ftCampaignAttendanceLastProcessedAt = nowMs;
            await processCampaignSessionAttendanceAfterApply(local);
        }
        planNextCampaignAttendanceSync(local);
        return;
    }

    const casterId = ftAttendanceCasterId();
    const cached = readCampaignAttendanceSessionStorage(campaignId);

    if (shouldSkipCampaignAttendanceNetworkFetch(cached)) {
        applyCampaignSessionAttendanceContext(cached!.ctx);
        await processCampaignSessionAttendanceAfterApply(cached!.ctx);
        planNextCampaignAttendanceSync(cached!.ctx);
        return;
    }

    const requestUrl = BASE_URL + 'options/tools/campaign_sessions_attended/get_campaign_session_attendance_context.php';

    try {
        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                campaign_id: campaignId,
                caster_id: casterId,
            }),
            credentials: 'same-origin',
            signal: AbortSignal.timeout(20000),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}, StatusText: ${response.statusText}`);
        }

        const responseText = await response.text();

        if (!responseText.trim()) {
            throw new Error('Empty response received from server.');
        }

        const ctx = attendanceContextOf(JSON.parse(responseText));
        if (!ctx) throw new Error('Invalid attendance response.');
        if (version !== ftAttendanceSyncVersion || getElementValue('campaign_id') !== campaignId) return;
        const serverNow = parseCampaignSessionUtc(ctx.server_utc_now);
        if (serverNow !== null) ftCampaignServerClockOffsetMs = Date.now() - serverNow;
        applyCampaignSessionAttendanceContext(ctx);
        writeCampaignAttendanceSessionStorage(campaignId, ctx);

        await processCampaignSessionAttendanceAfterApply(ctx);
        planNextCampaignAttendanceSync(ctx);
    } catch (error: unknown) {
        if (version !== ftAttendanceSyncVersion || getElementValue('campaign_id') !== campaignId) return;
        console.error(`[${new Date().toISOString()}] Error syncing campaign session attendance:`, error instanceof Error ? error.message : error, error);
        scheduleCampaignAttendanceSync(15 * 1000);
    }
}

function initCampaignSessionAttendancePolling(): void {
    if (ftAttendanceInitialized) { void syncCampaignSessionAttendance(); return; }
    ftAttendanceInitialized = true;
    const cid = getElementValue('campaign_id');
    if (cid && String(cid).trim() !== '') {
        hydrateCampaignAttendanceFromSessionStorage(String(cid).trim());
    }
    // A pushed campaign blob (ready click, inactive toggle, session edit, late worker, clock tick)
    // re-evaluates locally — this is the "no polling" path.
    window.addEventListener('ft:campaign-state-received', (event) => {
        const detail = (event as CustomEvent<{ campaignId?: string }>).detail;
        if (detail?.campaignId && detail.campaignId !== getElementValue('campaign_id')) return;
        // Capture the server clock offset from the frame that just arrived.
        const arr = (event as CustomEvent<{ campaignArr?: { computed?: { campaign?: { server_utc_now?: unknown } } } }>).detail?.campaignArr;
        const serverNow = parseCampaignSessionUtc(arr?.computed?.campaign?.server_utc_now);
        if (serverNow !== null) ftCampaignServerClockOffsetMs = Date.now() - serverNow;
        void syncCampaignSessionAttendance();
    });
    window.addEventListener('focus', () => { void syncCampaignSessionAttendance(); });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void syncCampaignSessionAttendance();
    });
    void syncCampaignSessionAttendance();
}

/** @deprecated Use syncCampaignSessionAttendance (single round-trip). */
async function fetchCampaignSessionStartAndEndTimes(): Promise<void> {
    await syncCampaignSessionAttendance();
}

/** @deprecated Use syncCampaignSessionAttendance (single round-trip). */
async function checkCampaignSessionAttended(): Promise<void> {
    await syncCampaignSessionAttendance();
}

let ftAttendancePrompt: AttendancePrompt | null = null;

async function markCampaignSessionAttended(): Promise<void> {
    const ctx = ftCampaignSessionAttendanceContext();
    const identity = {
        campaignId: String(getElementValue('campaign_id') || ''),
        sessionId: String(ctx.next_campaign_session_id || ''),
        casterId: String(ctx.caster_id || ftAttendanceCasterId()),
        sessionEnd: String(ctx.next_campaign_session_end_time || ''),
        deadline: parseCampaignSessionUtc(ctx.ready_deadline),
    };
    if (!identity.campaignId || !identity.sessionId || !ctx.require_ready_click || !ctx.in_attendance_window) return;
    if (ftAttendancePrompt && ['campaignId', 'sessionId', 'casterId'].every(key =>
        ftAttendancePrompt!.identity[key as keyof typeof identity] === identity[key as keyof typeof identity])) {
        ftAttendancePrompt.update(identity);
        return;
    }
    ftAttendancePrompt?.close();
    const prompt = createAttendancePrompt(identity, {
        endpoint: BASE_URL + 'options/tools/campaign_sessions_attended/mark_campaign_session_attended.php',
        now: () => Date.now() - ftCampaignServerClockOffsetMs,
        confirmed: (submitted) => {
            if (ftAttendancePrompt !== prompt || getElementValue('campaign_id') !== submitted.campaignId) return;
            ++ftAttendanceSyncVersion;
            ftSetCampaignSessionAttendanceContext({
                caster_active_in_campaign_session: '1', attended: 1, require_ready_click: false,
                confirmed_campaign_session_id: submitted.sessionId,
                confirmed_campaign_session_end_time: submitted.sessionEnd,
            }, 'attendance-marked');
            ftAttendancePrompt = null;
            planNextCampaignAttendanceSync(ftCampaignSessionAttendanceContext());
        },
        dismissed: () => {
            if (ftAttendancePrompt !== prompt) return;
            ftAttendancePrompt = null;
            ftSetCampaignSessionAttendanceContext({ caster_active_in_campaign_session: '' }, 'attendance-modal-hidden');
            scheduleCampaignAttendanceSync(60 * 1000);
        },
    });
    ftAttendancePrompt = prompt;
}
// ===========================================================================================
// Public surface — required by external callers (loadPostFunctions() in template.js + the PHP footer IIFE).
// All other top-level fns/consts are module-private (grep-verified: no cross-file consumer).
// ===========================================================================================
declare global {
    interface Window {
        FateTide: {
            sessionAttendance?: CampaignAttendanceContext;
        };
        ftGetCampaignSessionAttendance?: () => CampaignAttendanceContext | null;
        ftSetCampaignSessionAttendance?: (ctx: CampaignAttendanceContext, source: string) => CampaignAttendanceContext;
        ftGetMasterCasterId?: () => string;
        ftGetActiveCasterId?: () => string;
        initMerchantFavorGmPreview: typeof initMerchantFavorGmPreview;
        initializeVideoCarousels: typeof initializeVideoCarousels;
        initializeVideoCards: typeof initializeVideoCards;
        initCarouselButtonVisibility: typeof initCarouselButtonVisibility;
        initCampaignSessionAttendancePolling: typeof initCampaignSessionAttendancePolling;
        ftClearCampaignAttendanceSessionStorage: typeof ftClearCampaignAttendanceSessionStorage;
    }
}

window.initMerchantFavorGmPreview = initMerchantFavorGmPreview;
window.initializeVideoCarousels = initializeVideoCarousels;
window.initializeVideoCards = initializeVideoCards;
window.initCarouselButtonVisibility = initCarouselButtonVisibility;
window.initCampaignSessionAttendancePolling = initCampaignSessionAttendancePolling;
window.ftClearCampaignAttendanceSessionStorage = ftClearCampaignAttendanceSessionStorage;

export {};
